// src/utils/api.js
import http from '../api/http';

// Chuẩn hoá path: baseURL của http đã là '/api'
function norm(p) {
    p = String(p || '');
    p = p.replace(/^https?:\/\/[^/]+/i, '');
    while (p.startsWith('/')) p = p.slice(1);
    if (p.toLowerCase().startsWith('api/')) p = p.slice(4);
    return '/' + p;
}

async function req({ method = 'GET', url, data, params, headers }) {
    const res = await http.request({ method, url: norm(url), data, params, headers });
    return res.data;
}

function isRouteMissingOrNotAllowed(e) {
    const s = e?.response?.status;
    return s === 404 || s === 405; // Not Found / Method Not Allowed
}

/* ---------- SMART HELPERS ---------- */

// Thử nhiều biến thể cho UPDATE
async function smartUpdateUser(id, data) {
    const body = { ...data };
    // 1) PUT /admin/users/:id
    try { return await req({ method: 'PUT', url: `/admin/users/${id}`, data: body }); }
    catch (e1) {
        if (!isRouteMissingOrNotAllowed(e1)) throw e1;
        // 2) PATCH /admin/users/:id
        try { return await req({ method: 'PATCH', url: `/admin/users/${id}`, data: body }); }
        catch (e2) {
            if (!isRouteMissingOrNotAllowed(e2)) throw e2;
            // 3) POST /admin/users/:id  (nhiều backend cũ dùng POST để update)
            try { return await req({ method: 'POST', url: `/admin/users/${id}`, data: body }); }
            catch (e3) {
                if (!isRouteMissingOrNotAllowed(e3)) throw e3;
                // 4) POST /admin/users/update  (body phải có id)
                try { return await req({ method: 'POST', url: `/admin/users/update`, data: { id, ...body } }); }
                catch (e4) {
                    if (!isRouteMissingOrNotAllowed(e4)) throw e4;
                    // 5) Thử nhánh không admin
                    try { return await req({ method: 'PUT', url: `/users/${id}`, data: body }); }
                    catch (e5) {
                        if (!isRouteMissingOrNotAllowed(e5)) throw e5;
                        try { return await req({ method: 'PATCH', url: `/users/${id}`, data: body }); }
                        catch (e6) {
                            if (!isRouteMissingOrNotAllowed(e6)) throw e6;
                            try { return await req({ method: 'POST', url: `/users/${id}`, data: body }); }
                            catch (e7) {
                                if (!isRouteMissingOrNotAllowed(e7)) throw e7;
                                return await req({ method: 'POST', url: `/users/update`, data: { id, ...body } });
                            }
                        }
                    }
                }
            }
        }
    }
}

// Thử nhiều biến thể cho CREATE
async function smartCreateUser(data) {
    try { return await req({ method: 'POST', url: `/admin/users`, data }); }
    catch (e1) {
        if (!isRouteMissingOrNotAllowed(e1)) throw e1;
        try { return await req({ method: 'POST', url: `/users`, data }); }
        catch (e2) {
            if (!isRouteMissingOrNotAllowed(e2)) throw e2;
            // Dạng /admin/users/create hoặc /users/create
            try { return await req({ method: 'POST', url: `/admin/users/create`, data }); }
            catch (e3) {
                if (!isRouteMissingOrNotAllowed(e3)) throw e3;
                return await req({ method: 'POST', url: `/users/create`, data });
            }
        }
    }
}

// Thử nhiều biến thể cho DELETE
async function smartDeleteUser(id) {
    try { return await req({ method: 'DELETE', url: `/admin/users/${id}` }); }
    catch (e1) {
        if (!isRouteMissingOrNotAllowed(e1)) throw e1;
        try { return await req({ method: 'POST', url: `/admin/users/${id}/delete` }); }
        catch (e2) {
            if (!isRouteMissingOrNotAllowed(e2)) throw e2;
            try { return await req({ method: 'DELETE', url: `/users/${id}` }); }
            catch (e3) {
                if (!isRouteMissingOrNotAllowed(e3)) throw e3;
                return await req({ method: 'POST', url: `/users/${id}/delete` });
            }
        }
    }
}

/* ---------- PUBLIC API ---------- */

export async function api(path, opts = {}) {
    const { method = 'GET', data, params, headers } = opts;
    return req({ method, url: path, data, params, headers });
}

// ==== Auth ====
export const Auth = {
    me: () => api('/me'),
    login: (payload) => api('/login', { method: 'POST', data: payload }),
    logout: () => api('/logout', { method: 'POST' }),
};

// ==== Services (giữ nguyên) ====
export const Services = {
    list: (params) => api('/services', { params }),
    create: (data) => api('/services', { method: 'POST', data }),
    update: (id, data) => api(`/services/${id}`, { method: 'PUT', data }),
    del: (id) => api(`/services/${id}`, { method: 'DELETE' }),
};

// ==== Stats ====
export const Stats = {
    overview: () => api('/stats/overview'),
};

// ==== Forms (public) ====
export const Forms = {
    submit: ({ type, payload }) => api('/forms/submit', { method: 'POST', data: { type, payload } }),
};

// ==== Notifications (auth + perm) ====
export const Notifications = {
    list: (params) => api('/notifications', { params }),
    unreadCount: () => api('/notifications/unread-count'),
    markRead: (id) => api(`/notifications/${id}/read`, { method: 'POST' }),
    markAllRead: () => api('/notifications/read-all', { method: 'POST' }),
};

// ==== Users (LIST đơn giản; ghi dùng smart) ====
export const Users = {
    list: (params) => api('/users', { params }),
    create: (data) => smartCreateUser(data),
    update: (id, data) => smartUpdateUser(id, data),

    // SET PERMISSIONS (thay toàn bộ)
    setPermissions: (id, permissions) =>
        api(`/users/${id}/permissions`, { method: 'PUT', data: { permissions } }),

    // DELETE
    del: (id) => smartDeleteUser(id),
};

// Quyền hiển thị ở FE
export const PERMISSIONS = [
    { key: 'admin', label: 'Quản trị' },
    { key: 'users.manage', label: 'Quản lý user' },
    { key: 'services.manage', label: 'Quản lý dịch vụ' },
    { key: 'posts.manage', label: 'Duyệt/Xoá tin' },
    { key: 'notifications.view', label: 'Xem thông báo' },
];

// ==== Posts (giữ nguyên) ====
export const Posts = {
    list: (params) => api('/posts', { params }),
    approve: (id) => api(`/admin/posts/${id}/approve`, { method: 'POST' }),
    del: (id) => api(`/admin/posts/${id}`, { method: 'DELETE' }),
};

export { smartUpdateUser, smartCreateUser, smartDeleteUser };
export default http;
